import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface EducationSectionProps {
  className?: string;
}

interface Education {
  degree: string;
  field: string;
  minor?: string;
  institution: string;
  location: string;
  period: string;
  gpa: string;
  achievements: string[];
  highlights: string[];
}

const EducationSection: React.FC<EducationSectionProps> = ({ className }) => {
  const education: Education[] = [
    {
      degree: 'Master of Science',
      field: 'Software Engineering',
      institution: 'California State University, Fullerton',
      location: 'Fullerton, CA',
      period: '2019 - 2021',
      gpa: '4.0',
      achievements: [
        'Perfect 4.0 GPA - Graduated with highest academic distinction',
        'Advanced coursework in software architecture, design patterns, and system engineering',
        'Specialized in enterprise software development and scalable system design',
        'Completed comprehensive capstone project demonstrating full-stack development expertise',
      ],
      highlights: [
        'Software Architecture & Design',
        'Advanced Algorithms',
        'Database Systems',
        'Software Project Management',
        'System Engineering',
        'Quality Assurance',
      ],
    },
    {
      degree: 'Bachelor of Science',
      field: 'Computer Science',
      minor: 'Mathematics',
      institution: 'California State University, San Marcos',
      location: 'San Marcos, CA',
      period: '2017 - 2019',
      gpa: '3.8',
      achievements: [
        'Graduated Magna Cum Laude with 3.8 GPA',
        'Completed dual focus in Computer Science and Mathematics',
        'Strong foundation in theoretical computer science and mathematical principles',
        'Developed expertise in programming fundamentals and computational thinking',
      ],
      highlights: [
        'Data Structures & Algorithms',
        'Object-Oriented Programming',
        'Discrete Mathematics',
        'Linear Algebra',
        'Calculus & Statistics',
        'Computer Systems',
      ],
    },
  ];

  return (
    <section id='education' className={cn('py-20 bg-background', className)}>
      <div className='container mx-auto px-4'>
        {/* Section Header */}
        <div className='text-center mb-16'>
          <h2 className='text-3xl sm:text-4xl font-bold text-foreground mb-4'>
            Education
          </h2>
          <p className='text-lg text-muted-foreground max-w-3xl mx-auto'>
            Strong academic foundation in software engineering and computer
            science, with exceptional academic performance and comprehensive
            technical training
          </p>
        </div>

        {/* Education Cards */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16'>
          {education.map((edu, index) => (
            <Card
              key={index}
              className='hover:shadow-lg transition-shadow duration-300 h-full'
            >
              <CardHeader className='pb-4'>
                <div className='flex items-start justify-between mb-4'>
                  <div className='flex-1'>
                    <CardTitle className='text-xl text-primary mb-2'>
                      {edu.degree}
                    </CardTitle>
                    <h3 className='text-lg font-semibold text-foreground mb-1'>
                      {edu.field}
                    </h3>
                    {edu.minor && (
                      <p className='text-sm text-muted-foreground mb-2'>
                        Minor: {edu.minor}
                      </p>
                    )}
                    <p className='text-base font-medium text-foreground'>
                      {edu.institution}
                    </p>
                    <p className='text-sm text-muted-foreground'>
                      {edu.location}
                    </p>
                  </div>
                  <div className='text-right ml-4'>
                    <Badge variant='outline' className='mb-2'>
                      {edu.period}
                    </Badge>
                    <div className='text-right'>
                      <div className='text-2xl font-bold text-primary'>
                        {edu.gpa}
                      </div>
                      <div className='text-xs text-muted-foreground'>GPA</div>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className='space-y-6'>
                {/* Academic Achievements */}
                <div>
                  <h4 className='text-sm font-semibold text-foreground mb-3'>
                    Academic Achievements
                  </h4>
                  <ul className='space-y-2'>
                    {edu.achievements.map((achievement, achievementIndex) => (
                      <li
                        key={achievementIndex}
                        className='flex items-start gap-2'
                      >
                        <span className='text-primary mt-1.5 text-xs'>▸</span>
                        <span className='text-sm text-muted-foreground leading-relaxed'>
                          {achievement}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Key Coursework */}
                <div>
                  <h4 className='text-sm font-semibold text-foreground mb-3'>
                    Key Coursework
                  </h4>
                  <div className='flex flex-wrap gap-2'>
                    {edu.highlights.map(course => (
                      <Badge
                        key={course}
                        variant='secondary'
                        className='text-xs'
                      >
                        {course}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Academic Summary */}
        <div className='text-center'>
          <h3 className='text-2xl font-semibold text-foreground mb-6'>
            Academic Excellence
          </h3>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto'>
            <div className='text-center'>
              <div className='text-3xl font-bold text-primary mb-2'>4.0</div>
              <div className='text-sm text-muted-foreground'>Master's GPA</div>
              <div className='text-xs text-muted-foreground mt-1'>
                Perfect Score
              </div>
            </div>
            <div className='text-center'>
              <div className='text-3xl font-bold text-primary mb-2'>3.8</div>
              <div className='text-sm text-muted-foreground'>
                Bachelor's GPA
              </div>
              <div className='text-xs text-muted-foreground mt-1'>
                Magna Cum Laude
              </div>
            </div>
            <div className='text-center'>
              <div className='text-3xl font-bold text-primary mb-2'>2</div>
              <div className='text-sm text-muted-foreground'>
                Degrees Earned
              </div>
              <div className='text-xs text-muted-foreground mt-1'>
                CSU System
              </div>
            </div>
          </div>
        </div>

        {/* Educational Philosophy */}
        <div className='mt-16 text-center max-w-4xl mx-auto'>
          <Card className='bg-muted/30 border-none'>
            <CardContent className='pt-8'>
              <h3 className='text-xl font-semibold text-foreground mb-4'>
                Continuous Learning Mindset
              </h3>
              <p className='text-muted-foreground leading-relaxed'>
                My academic journey reflects a commitment to excellence and
                continuous learning. The combination of theoretical computer
                science knowledge and practical software engineering skills
                provides a strong foundation for solving complex technical
                challenges and leading innovative projects.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default EducationSection;
